import Link from "next/link";

import { ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { SUBSCRIPTION_PLANS } from "@/lib/constants/pricing";
import { cn } from "@/lib/utils";

import type { SubscriptionPlan } from "@/lib/constants/pricing";

function ComparePricingButton({ className }: { className?: string }) {
  return (
    <Button asChild variant="rebolt-outline" size="xl" className={className}>
      <Link href="/pricing">Compare pricing</Link>
    </Button>
  );
}

function PricingCard({ plan }: { plan: SubscriptionPlan }) {
  const isDark = !!plan.popular;
  const { home } = plan;

  return (
    <div
      className={cn(
        "flex flex-col justify-between rounded-3xl border p-8",
        isDark
          ? "bg-ash-900 border-ash-100 md:flex-1 shadow-md w-full lg:w-90 md:min-h-[428px]"
          : "bg-clay-100 border-ash-100 w-full lg:w-75 md:min-h-[374px]"
      )}
    >
      <div className="flex flex-col gap-4">
        <span
          className={cn("text-xs font-bold uppercase tracking-[1.1px]", isDark ? "text-rebolt-500" : "text-clay-400")}
        >
          {home.eyebrow}
        </span>

        <h3 className={cn("text-2xl font-extrabold tracking-[-0.5px]", isDark ? "text-white" : "text-ash-900")}>
          {home.name}
        </h3>

        <p className={cn("text-sm leading-[1.3]", isDark ? "text-clay-300" : "text-clay-600")}>{home.description}</p>

        <div className="flex items-start gap-0.75">
          <span
            className={cn("text-xl font-bold leading-none self-end pb-0.75", isDark ? "text-clay-300" : "text-ash-600")}
          >
            $
          </span>
          <span
            className={cn(
              "text-5xl font-extrabold leading-none tracking-[-0.75px]",
              isDark ? "text-white" : "text-ash-900"
            )}
          >
            {plan.pricing.yearly}
          </span>
          <div className="flex flex-col justify-end pt-1.5">
            <span className={cn("text-sm font-extrabold", isDark ? "text-clay-400" : "text-ash-600")}>per month</span>
            <span className={cn("text-[10px] font-semibold", isDark ? "text-ash-300" : "text-clay-400")}>
              billed yearly
            </span>
          </div>
        </div>
      </div>

      {isDark ? (
        <Button asChild variant="rebolt-primary" size="xl" className="mt-8 w-full rounded-full">
          <Link href={plan.ctaLink} className="inline-flex w-full items-center justify-center gap-2">
            {home.cta}
            <ChevronRight className="w-5 h-5 shrink-0" />
          </Link>
        </Button>
      ) : (
        <Button asChild variant="rebolt-outline" size="xl" className="mt-8 w-full rounded-full">
          <Link href={plan.ctaLink}>{home.cta}</Link>
        </Button>
      )}
    </div>
  );
}

export default function PricingSection() {
  return (
    <section className="w-full bg-white px-5 py-8 md:py-22">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 md:gap-11">
        {/* header */}
        <div className="flex flex-col items-center gap-1 md:gap-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[1px] text-rebolt-500">Pricing</span>

          <h2 className="font-clash text-4xl lg:text-6xl font-medium leading-none tracking-[-1.2px] text-ash-900">
            Simple. Efficient.
            <br />
            Doesn&apos;t require a Math Degree.
          </h2>

          <p className="text-base leading-[1.3] text-ash-600">
            Tired of being nickeled and dimed? We heard you.
            <br />
            We&apos;ve built a pricing model that&apos;s simple, transparent, and fair.
          </p>
        </div>
        {/* <ComparePricingButton className="block lg:hidden" /> */}

        {/* pricing cards — popular first on mobile, original order on desktop */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-end justify-center gap-5 w-full max-w-5xl">
          {SUBSCRIPTION_PLANS.map((plan) => (
            <div
              key={plan.id}
              className={cn(
                plan.id === "marketing-kit" && "order-2 lg:order-0",
                plan.id === "mk-crm-bundle" && "order-1 lg:order-0",
                plan.id === "crm" && "order-3 lg:order-0"
              )}
            >
              <PricingCard plan={plan} />
            </div>
          ))}
        </div>

        {/* footer */}
        <div className="flex flex-col items-center gap-4 md:gap-11">
          <p className="text-sm text-clay-500">No contracts. Cancel anytime.</p>
          {/* <ComparePricingButton className="hidden lg:block" /> */}
        </div>
      </div>
    </section>
  );
}
